/* 3D 엔진 실행. 2D 엔진(run.ts)과 같은 호스트 규약(요청 하나에 응답 하나, 단계별 캡처, 판정, 해설 형식)을 따르고 도구만 math3d 명령이다.
 * 구간: 1 풀이와 그림 계획(글), 2 풀이 글에서 값 옮기기(JSON), 3 구성(호출 JSON), 측정과 독립 재계산은 코드가 한다 */
import { seconds } from './model';
import type { ChatMessage, ModelPort } from './model';
import type { Hooks } from './run';
import { FIGURE_DECLARATION_RULE, ITEM_FORMAT_RULE, SOLUTION_FORMAT, VARIANT_ELEMENTS, WRITING_RULE, readDeclarations, splitWritten } from './solution';
import { CSAT_FORMAT_KNOWLEDGE, similarPastItems } from './knowledge';
import { answerFormatIssue, judge, problemPointNames, readSolved } from './solve';
import type { Acceptance } from './solve';
import { similarityPercent, verifyAgainstMeasured } from './measure';
import { judgeIndependent } from './independent';
import type { Independent } from './independent';
import { cleanModelText, restoreEscapes } from './symbols';
import { normalizeDelimiters } from './segments';
import { allObjects3d, executeOne3d, registryOf3d } from './tools/executor3d';
import type { Math3DApp } from './tools/executor3d';
import { TOOL_NAMES3D, callsSchema3d, drawCalls3d, inPalette3d, menuText3d, paletteText3d, sceneState3d } from './tools/contract3d';
import type { Drawn3 } from './tools/contract3d';
import { angleDegrees3, distance3, pointPlaneDistance3, polygonArea3, pyramidVolume3 } from './independent3d';
import { fitViewFromFigure } from './figureview3d';
import type { FigurePoint, ViewFit } from './figureview3d';
import type { V3 } from './independent3d';
import type { ToolCall } from './tools/types';

export interface Hooks3 extends Hooks {
  /** 세워진 호출을 세운 순서대로 알린다. 단계 캡처가 끝 카메라로 다시 세워 찍는 재료이다 */
  recordCalls?: (calls: ToolCall[]) => void;
  /** 호출이 count 개 선 순간의 프레임과 풀이 단계 표시. 그림은 끝에서 앞의 count 개만 다시 세워 찍는다 */
  /** 문항 그림에서 복원한 시점. 없으면 null(엔진 기본 시점) */
  setView?: (view: ViewFit | null) => void;
  markFrame?: (names: string[], label: string, count: number) => void;
  markStep?: (step: number, label: string, names: string[], count: number) => void;
}

const MAX_GENERATE = 65536;
const BASIC_TOOLS = ['object.addPoint', 'object.addSegment', 'object.addPolygon', 'object.setLinearDashed'];

export const TARGET3_KINDS = ['none', 'length', 'angle', 'area_polygon', 'volume_pyramid', 'distance_point_plane'] as const;
export type Target3Kind = typeof TARGET3_KINDS[number];
export interface Target3 { kind: Target3Kind; points: string[] }
const NO_TARGET3: Target3 = { kind: 'none', points: [] };

const VARIANT_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: { problem: { type: 'string' }, changes: { type: 'array', items: { type: 'string' }, minItems: 1, maxItems: 4 } },
  required: ['problem', 'changes'],
  additionalProperties: false,
};

function variantPrompt(elements: string[]): string {
  return [
    '기준 기출 문항 하나를 받아 같은 구조, 같은 묻는 것의 새 문항을 한 단계만 변형해 만든다. JSON 으로만 답한다.',
    `- 바꾸는 것: ${elements.length ? elements.join(', ') : `${VARIANT_ELEMENTS.join(', ')} 가운데 둘에서 넷`}. 그 밖은 원본 그대로.`,
    '- problem: 새 문항 원문(한국어, 식은 $…$ LaTeX). 답이 깔끔한 값이 되게 수를 고른다. 이 문항은 그림이 서고 잰 값이 답과 맞을 때만 채택된다.',
    ITEM_FORMAT_RULE,
    '- changes: 바꾼 것을 한국어로 하나에서 넷.',
  ].join('\n');
}

function writePrompt3d(): string {
  return [
    '너는 대한민국 수능 수학의 공간도형 문항을 풀고, 그 문제를 mathcore 3D 스케치북에 어떻게 그려 낼지까지 계획한다. JSON 이 아니라 글로 답한다.',
    `- 글은 세 절로 쓴다. 첫 절은 「${SOLUTION_FORMAT.steps}」 머리로 시작한다. ${SOLUTION_FORMAT.stepsRule} ${FIGURE_DECLARATION_RULE}`,
    `- 둘째 절은 「${SOLUTION_FORMAT.quick}」 머리로 시작한다. ${SOLUTION_FORMAT.quickRule}`,
    `- 셋째 절은 「${SOLUTION_FORMAT.plan}」 머리로 시작한다. 이 문제를 어떻게 그리고 무엇을 재서 답과 대조할지 순서대로 쓴다. 쓸 도구는 아래 「색칠도구」 목록에서 고른다.`,
    '  - 그림은 좌표공간에 세운다. 문항이 도형의 크기나 위치를 정하면 풀이가 그 조건을 만족하는 좌표를 직접 정해 점을 좌표로 찍고(object.addPoint), 선, 면, 입체는 그 점을 받는 도구로 세운다. 점의 이름은 문항이 붙인 이름과 같게 한다.',
    '  - 도구마다 「받는 것」이 있다. 받는 개체가 먼저 서 있어야 한다. 수선의 발, 교점, 평면처럼 mathcore 가 계산해 주는 것은 풀이가 계산한 좌표를 찍지 않고 그 도구로 만든다.',
    '  - 문항이 구하라는 양(길이, 각, 넓이, 부피, 점과 평면 사이의 거리)은 엔진이 직접 세워 잰다. 어떤 양을 어떤 점으로 잴지 풀이에 적는다.',
    '  - 문항에 그림이 있으면 그 그림의 모양을 그대로 옮긴다. 좌표는 문항의 조건으로 정하면 되고 그림의 방향과 맞출 필요가 없다(엔진이 그림에서 읽은 꼭짓점 위치로 시점을 맞춘다). 그림에서 가려져 점선으로 그려진 모서리는 점선으로 만들 계획을 쓴다.',
    '  - 마지막 줄에 「도구: 이름, 이름」을 쓴다. 목록에 있는 이름만 쓰고 같은 이름은 한 번만 쓴다.',
    '- 풀이의 마지막 줄(그림 계획 앞)에 「답: 값」을 쓴다. 객관식이면 고른 선지(①~⑤)와 그 값을 함께 쓴다.',
    `- ${WRITING_RULE}`,
    '',
    '## 색칠도구 (이름: 한글 이름 - 설명 → 만드는 개체 · 받는 것)',
    menuText3d(),
  ].join('\n');
}

function extractPrompt3d(): string {
  return [
    '너는 수능 수학 공간도형 문항의 풀이 글에서 값을 옮긴다. 풀이 글에 없는 값은 만들지 않는다. JSON 으로만 답한다.',
    '- figure: 문항에 그릴 그림(공간의 점, 선, 면, 입체)이 있으면 「있음」, 식만 다루면 「없음」.',
    '- target: 문항이 구하라는 양을 선언한다. 엔진이 그 선언으로 측정 개체를 직접 세워 잰다. points 는 그림 선언의 점 이름이다.',
    '  - length: points = [점, 점]. 두 점 사이의 거리.',
    '  - angle: points = [점, 꼭짓점, 점]. 꼭짓점에서 두 점이 이루는 각(도).',
    '  - area_polygon: points = 다각형의 꼭짓점을 순서대로. 그 다각형의 넓이.',
    '  - volume_pyramid: points = 밑면 꼭짓점을 순서대로, 마지막에 꼭짓점(꼭대기). 그 각뿔의 부피.',
    '  - distance_point_plane: points = [점, 평면 위의 세 점]. 점에서 그 평면까지의 거리.',
    '  - none: 위 어느 것도 아니다.',
    '- choice: 풀이 글이 고른 선지(①~⑤), 단답형이면 빈 문자열. value: 풀이 글의 답. 수로 옮기거나 풀이 글의 식(LaTeX)을 그대로 옮긴다. 답은 「그림 계획」 앞의 「답:」 줄이다.',
    '- measure_value: 그림에서 잴 값. 답이 곧 잴 값이면 답과 같은 값. 답이 p+q 처럼 잰 값에서 유도되면 그림에서 실제로 재는 양(예: 거리 4/3 이면 "\\frac{4}{3}").',
    '- tools: 풀이 글의 「그림 계획」 마지막 줄 「도구:」에 적힌 도구 이름. 글에 없는 이름은 만들지 않는다.',
    '- figure_points: 문항 그림에서 이름 붙은 꼭짓점이 그려진 위치. 그림의 왼쪽 위를 (0, 0) 으로 하고 그림의 가로 길이를 1 로 해서 x 와 y 모두 그 길이에 대한 비율로 적는다(y 는 아래로 커진다). x 와 y 는 같은 눈금이어야 한다. 한 평면 위에 있지 않은 점을 네 개 이상 고르고, 그림이 없거나 위치를 읽을 수 없으면 빈 배열.',
  ].join('\n');
}

function solveSchema3d(): Record<string, unknown> {
  return {
    type: 'object',
    properties: {
      figure: { type: 'string', enum: ['있음', '없음'] },
      choice: { type: 'string', enum: ['', '①', '②', '③', '④', '⑤'], description: '객관식이면 고른 선지, 단답형이면 빈 문자열' },
      value: { anyOf: [{ type: 'number' }, { type: 'string' }], description: '답. 수, 또는 풀이 글의 식(LaTeX). 객관식이면 고른 선지의 값' },
      measure_value: { anyOf: [{ type: 'number' }, { type: 'string' }], description: '그림에서 잴 값. 답이 곧 잴 값이면 답과 같은 값' },
      target: { type: 'object', properties: { kind: { type: 'string', enum: [...TARGET3_KINDS] }, points: { type: 'array', items: { type: 'string' }, maxItems: 8 } }, required: ['kind', 'points'], additionalProperties: false },
      tools: { type: 'array', items: { type: 'string', enum: [...TOOL_NAMES3D] }, maxItems: 16, description: '풀이 글의 「그림 계획」이 정한 도구 이름. 없으면 빈 배열' },
      figure_points: { type: 'array', maxItems: 24, items: { type: 'object', properties: { name: { type: 'string' }, x: { type: 'number' }, y: { type: 'number' } }, required: ['name', 'x', 'y'], additionalProperties: false }, description: '문항 그림에서 이름 붙은 꼭짓점의 위치(그림 가로 길이를 1 로 한 비율, y 는 아래로). 없으면 빈 배열' },
    },
    required: ['figure', 'choice', 'value', 'measure_value', 'target', 'tools', 'figure_points'],
    additionalProperties: false,
  };
}

function composePrompt3d(tools: string[]): string {
  return [
    '너는 풀이한 수능 수학 공간도형 문항을 mathcore 3D 스케치북에 그린다. 이번 단계는 「구성」 단계다. calls 에 호출을 순서대로 적는다. tool 은 아래 목록의 이름, args 는 그 도구의 인자다. 선택 인자는 null 로 둘 수 있다.',
    '- 개체를 가리키는 인자는 이 JSON 에서 먼저 세운 개체의 name 으로 쓴다. 이미 선 개체는 사용자 글의 「이미 선 개체」에 있다. 세우지 않은 이름은 가리키지 않는다.',
    '- 점은 풀이가 정한 좌표로 찍는다(position 은 [x, y, z]). 점의 name 은 문항과 풀이의 그림 선언과 같은 이름이다. 선, 면, 입체는 그 점의 name 으로 받는다.',
    '- 구하는 양을 재는 개체(거리, 각, 넓이, 부피)는 세우지 않는다. 엔진이 선언된 target 으로 직접 세워 잰다.',
    '- 문항 그림에서 가려져 점선으로 그려진 모서리는 그 선분을 먼저 세운 뒤 object.setLinearDashed 로 dashed 를 true 로 한다.',
    '',
    '## 색칠도구 사용법 (취합한 것)',
    paletteText3d(tools),
  ].join('\n');
}

interface Asked { raw: Record<string, unknown>; ms: number; tokens: number | undefined }

function modelPort(hooks: Hooks3): ModelPort {
  if (!hooks.model) throw new Error('모델 포트가 없다. MCP 호스트가 pipeline_start 로 시작한다');
  return hooks.model;
}

async function askOnce(messages: ChatMessage[], schema: Record<string, unknown>, signal: AbortSignal, hooks: Hooks3, label: string): Promise<{ asked: Asked } | { problem: string; head: string }> {
  const started = Date.now();
  const answer = await modelPort(hooks)(messages, { label, signal, think: false, format: schema, numPredict: MAX_GENERATE }, (t) => hooks.replaceThinking(t), (c) => hooks.setStatus(`${label} 수신 중 ${c.length}자`));
  if (answer.doneReason === 'length') return { problem: `${label}이 토큰 한도에서 잘렸다`, head: answer.content.slice(0, 600) };
  let value: unknown;
  try { value = restoreEscapes(JSON.parse(answer.content)); } catch (error) { return { problem: `${label} JSON 을 읽지 못했다: ${(error as Error).message}`, head: answer.content.slice(0, 300) }; }
  if (!value || typeof value !== 'object') return { problem: `${label} 이 객체가 아니다`, head: answer.content.slice(0, 300) };
  return { asked: { raw: value as Record<string, unknown>, ms: Date.now() - started, tokens: answer.evalCount } };
}

async function askJson(messages: ChatMessage[], schema: Record<string, unknown>, signal: AbortSignal, hooks: Hooks3, label: string): Promise<Asked> {
  const first = await askOnce(messages, schema, signal, hooks, label);
  if ('asked' in first) return first.asked;
  hooks.push('error', `${first.problem}. 본문 머리: ${first.head}`);
  const retry: ChatMessage[] = [...messages, { role: 'assistant', content: first.head }, { role: 'user', content: `앞 답은 쓸 수 없었다: ${first.problem}. 같은 스키마의 JSON 하나만, 더 짧게 다시 낸다.` }];
  const second = await askOnce(retry, schema, signal, hooks, `${label} 재요청`);
  if ('asked' in second) return second.asked;
  throw new Error(`${second.problem}. 본문 머리: ${second.head}`);
}

async function askText(messages: ChatMessage[], signal: AbortSignal, hooks: Hooks3, label: string): Promise<{ text: string; ms: number; tokens: number | undefined }> {
  const started = Date.now();
  const answer = await modelPort(hooks)(messages, { label, signal, think: false, numPredict: MAX_GENERATE }, (t) => hooks.replaceThinking(t), (c) => hooks.setStatus(`${label} 수신 중 ${c.length}자`));
  const text = answer.content.trim();
  if (answer.doneReason === 'length') hooks.push('error', `${label}이 토큰 한도에서 잘렸다. 받은 글까지로 다음 단계를 한다`);
  if (!text) throw new Error(`${label}이 빈 글이다`);
  return { text, ms: Date.now() - started, tokens: answer.evalCount };
}

function readTarget3(raw: unknown): Target3 {
  const o = raw as { kind?: unknown; points?: unknown } | null;
  if (!o || typeof o !== 'object' || !TARGET3_KINDS.includes(o.kind as Target3Kind)) return NO_TARGET3;
  return { kind: o.kind as Target3Kind, points: Array.isArray(o.points) ? o.points.filter((p): p is string => typeof p === 'string') : [] };
}

const call3 = (name: string, args: Record<string, unknown>, i: number): ToolCall => ({ id: `t${i}`, type: 'function', function: { name, arguments: JSON.stringify(args) } });

/** 구하는 양을 엔진이 직접 세워 잰다. 보조 개체의 이름은 target_ 로 시작한다. 실패하면 사유를 돌려준다 */
async function measureTarget3(app: Math3DApp, target: Target3): Promise<{ calls: ToolCall[]; problem: string }> {
  const p = target.points;
  const plan: ToolCall[] = [];
  const need = (n: number, exact = true): string => (exact ? p.length === n : p.length >= n) ? '' : `${target.kind} 의 점이 ${exact ? '' : '최소 '}${n}개여야 한다(받은 ${p.length}개)`;
  let problem = '';
  if (target.kind === 'length') { problem = need(2); plan.push(call3('object.addMeasurement', { category: 'measureLength', relativeObjects: p }, 0)); }
  else if (target.kind === 'angle') { problem = need(3); plan.push(call3('object.addMeasurement', { category: 'measureAngle', relativeObjects: p }, 0)); }
  else if (target.kind === 'area_polygon') { problem = need(3, false); plan.push(call3('object.addPolygon', { pointIDs: p, name: 'target_polygon' }, 0), call3('object.addMeasurement', { category: 'measureArea', relativeObjects: ['target_polygon'] }, 1)); }
  else if (target.kind === 'volume_pyramid') {
    problem = need(4, false);
    plan.push(call3('object.addPolygon', { pointIDs: p.slice(0, -1), name: 'target_base' }, 0), call3('object.addPyramidFromPolygon', { polygonID: 'target_base', topVertexPointID: p[p.length - 1], name: 'target_solid' }, 1), call3('object.addMeasurement', { category: 'measureVolume', relativeObjects: ['target_solid'] }, 2));
  } else if (target.kind === 'distance_point_plane') {
    problem = need(4);
    plan.push(call3('object.addPlaneFromThreePoints', { pointID1: p[1], pointID2: p[2], pointID3: p[3], name: 'target_plane' }, 0), call3('object.addPerpendicularLine', { pointID1: p[0], referenceID1: 'target_plane', name: 'target_perp' }, 1), call3('object.addPointIntersection', { referenceID1: 'target_perp', referenceID2: 'target_plane', name: 'target_foot' }, 2), call3('object.addMeasurement', { category: 'measureLength', relativeObjects: [p[0], 'target_foot'] }, 3));
  }
  if (problem) return { calls: [], problem };
  const registry = registryOf3d(app);
  const done: ToolCall[] = [];
  for (const call of plan) {
    const r = await executeOne3d(app, call, registry);
    if (!r.ok) return { calls: done, problem: `${call.function.name}: ${r.detail}` };
    done.push(call);
  }
  return { calls: done, problem: '' };
}

/** 구하는 양을 장면의 좌표에서 다시 계산한다 */
function independentValue3(app: Math3DApp, target: Target3): Independent {
  const registry = registryOf3d(app);
  const none = (note: string): Independent => ({ value: null, note });
  if (target.kind === 'none') return none('구하는 양이 선언되지 않아 독립 재계산을 못 했다');
  const pts = target.points.map((n) => registry.objectOf(n)?.position);
  if (!pts.length || pts.some((q) => !q || q.length !== 3)) return none(`점 ${target.points.join(', ') || '(없음)'} 의 좌표를 읽지 못했다`);
  const v = pts as V3[];
  const names = target.points.join(', ');
  if (target.kind === 'length') return v.length === 2 ? { value: distance3(v[0], v[1]), note: `점 ${names} 사이 거리` } : none('길이의 점이 두 개가 아니다');
  if (target.kind === 'angle') { if (v.length !== 3) return none('각의 점이 세 개가 아니다'); const deg = angleDegrees3(v[0], v[1], v[2]); return { value: deg, alt: (deg * Math.PI) / 180, note: `점 ${target.points[1]} 에서 ${target.points[0]}, ${target.points[2]} 가 이루는 각(도, 호도 후보)` }; }
  if (target.kind === 'area_polygon') return v.length >= 3 ? { value: polygonArea3(v), note: `꼭짓점 ${names} 의 넓이` } : none('다각형의 꼭짓점이 셋 미만이다');
  if (target.kind === 'volume_pyramid') return v.length >= 4 ? { value: pyramidVolume3(v.slice(0, -1), v[v.length - 1]), note: `밑면 ${target.points.slice(0, -1).join(', ')} 과 꼭짓점 ${target.points[target.points.length - 1]} 의 각뿔 부피` } : none('각뿔의 점이 넷 미만이다');
  if (target.kind === 'distance_point_plane') return v.length === 4 ? { value: pointPlaneDistance3(v[0], v[1], v[2], v[3]), note: `점 ${target.points[0]} 에서 평면 ${target.points.slice(1).join(', ')} 까지 거리` } : none('점과 평면의 점이 넷이 아니다');
  return none('알 수 없는 구하는 양');
}

/** 장면의 측정 개체들(값이 유효한 것) */
function measuredOf(app: Math3DApp): { name: string; kind: string; value: number }[] {
  return allObjects3d(app).filter((o) => o.type === 'measurement' && o.isValid !== false && Number.isFinite(o.value)).map((o) => ({ name: o.name ?? '', kind: o.category ?? 'measurement', value: Number(o.value) }));
}

export async function runDemo3d(app: Math3DApp, problem: string, signal: AbortSignal, hooks: Hooks3, variant?: { elements: string[] }): Promise<string> {
  const startedAt = Date.now();
  let target = problem;
  if (variant) {
    hooks.setStatus('변형 문항 작성 (호스트 모델)');
    const got = await askJson([{ role: 'system', content: variantPrompt(variant.elements) }, { role: 'user', content: `## 원본 문항\n${problem}` }], VARIANT_SCHEMA, signal, hooks, '변형');
    target = cleanModelText(String(got.raw.problem ?? '')).trim();
    if (!target) throw new Error('변형 문항을 받지 못했다');
    const changes = Array.isArray(got.raw.changes) ? got.raw.changes.map((c) => String(c ?? '').trim()).filter(Boolean) : [];
    hooks.push('meta', `변형 ${seconds(got.ms)} · 생성 ${got.tokens ?? '?'} 토큰`);
    hooks.push('assistant', `변형 문항(검증 전):\n${target}`);
    hooks.setMeta({ mode: 'variant', variantText: problem, changes, similarity: similarityPercent(problem, target) });
  }
  const timeline: { label: string; at: number }[] = [];
  const mark = (label: string): void => { timeline.push({ label, at: Date.now() - startedAt }); hooks.setMeta({ timeline: [...timeline] }); };

  const similar = await similarPastItems(target);
  const refs = similar.length ? ['## 유사 기출 참고(출제 논리를 참고한다. 베끼지 않고 정답을 가정하지 않는다)', ...similar.map((s, i) => `${i + 1}. [${s.tags.join(', ') || '태그 없음'}] ${s.text.replace(/\s+/g, ' ').slice(0, 600)}`)].join('\n') : '';
  const background = [CSAT_FORMAT_KNOWLEDGE, refs].filter(Boolean).join('\n\n');
  hooks.setMeta({ background: { similar: similar.length, experience: { recipes: 0, failures: 0, notes: 0, total: 0 } } });
  hooks.push('meta', `배경 지식 · 수능 형식 규칙 · 유사 기출 참고 ${similar.length}개 (3D 는 이전 경험을 쓰지 않음)`);
  mark('배경 지식');

  hooks.setStatus('1/3 추론 · 풀이와 그림 계획 (호스트 모델)');
  const wrote = await askText([{ role: 'system', content: `${writePrompt3d()}\n\n${background}` }, { role: 'user', content: target }], signal, hooks, '풀이와 그림 계획');
  mark('풀이와 그림 계획');
  hooks.push('meta', `풀이와 그림 계획 ${seconds(wrote.ms)} · 생성 ${wrote.tokens ?? '?'} 토큰`);
  hooks.push('assistant', `풀이 글:\n${wrote.text}`);

  hooks.setStatus('2/3 추론 · 풀이 글에서 값 옮기기 (호스트 모델)');
  const got1 = await askJson([{ role: 'system', content: extractPrompt3d() }, { role: 'user', content: `## 문항\n${target}\n\n## 풀이 글\n${wrote.text}` }], solveSchema3d(), signal, hooks, '값 옮기기');
  mark('값 옮기기');
  const solved = readSolved({ ...got1.raw, values: {}, target: undefined }, [], wrote.text);
  const target3 = readTarget3(got1.raw.target);
  const tools = inPalette3d(Array.isArray(got1.raw.tools) ? got1.raw.tools : []);
  const figurePoints: FigurePoint[] = (Array.isArray(got1.raw.figure_points) ? got1.raw.figure_points as Record<string, unknown>[] : [])
    .filter((p) => p && typeof p.name === 'string' && Number.isFinite(p.x) && Number.isFinite(p.y)).map((p) => ({ name: String(p.name), x: Number(p.x), y: Number(p.y) }));
  hooks.push('meta', `값 옮기기 ${seconds(got1.ms)} · 그림 ${solved.figure} · 구하는 양 ${target3.kind}(${target3.points.join(',')}) · 도구 ${tools.length}개 · 생성 ${got1.tokens ?? '?'} 토큰`);
  for (const issue of solved.issues) hooks.push('error', issue);

  const written = splitWritten(wrote.text);
  const declaredRead = readDeclarations(cleanModelText(normalizeDelimiters(written.steps)));
  const steps = declaredRead.text;
  const quick = cleanModelText(normalizeDelimiters(written.quick));
  const declared = declaredRead.declared;
  hooks.setMeta({ declared });
  hooks.push('meta', `그림 선언 ${declared.filter((d) => d.length).length}/${declared.length} 단계 · ${declared.map((d, i) => `${i + 1}:[${d.join(',')}]`).join(' ')}`);
  const answer = solved.answer;
  const value = solved.value;
  hooks.push('assistant', `정답(모델): ${answer || '(없음)'}`);

  if (solved.figure === '없음') {
    const j0 = judge({ figure: '없음', failCount: 0, missingPoints: [], verdict: 'none', detail: '', answerIssue: answerFormatIssue(target, solved) });
    const elapsed0 = seconds(Date.now() - startedAt);
    hooks.setMeta({ answer, acceptance: j0.acceptance, acceptanceReason: j0.reason, elapsed: elapsed0 });
    hooks.setSolution([`**판정: ${j0.acceptance}** — ${j0.reason}`, '', SOLUTION_FORMAT.figure, '', '(그림 없는 문항)', '', SOLUTION_FORMAT.steps, '', steps || '(없음)', '', SOLUTION_FORMAT.quick, '', quick || '(없음)', '', `**정답:** ${answer || '(없음)'}`].join('\n'));
    const stamp0 = `${elapsed0} · ${j0.acceptance} · 그림 없음`;
    hooks.push(j0.acceptance === '불성립' ? 'error' : 'assistant', `끝. ${stamp0}`);
    return stamp0;
  }

  // 호출 하나가 선 뒤마다 단계 경계와 프레임을 알린다. 선언한 이름이 전부 선 풀이 단계는 그 순간을 단계 캡처로 남긴다
  const capturedSteps = new Set<number>();
  const afterEachCall = (standingNames: string[], label: string, count: number): void => {
    hooks.markFrame?.(standingNames, label, count);
    const have = new Set(standingNames);
    for (let i = 0; i < declared.length; i += 1) {
      if (capturedSteps.has(i + 1) || !declared[i].length) continue;
      if (declared[i].every((n) => have.has(n))) { capturedSteps.add(i + 1); hooks.markStep?.(i + 1, label, declared[i], count); }
    }
  };

  hooks.setStatus('3/3 추론 · 구성 (호스트 모델)');
  const chosen = [...new Set([...BASIC_TOOLS, ...tools])];
  const got3 = await askJson([{ role: 'system', content: composePrompt3d(chosen) }, { role: 'user', content: `## 문항\n${target}\n\n## 풀이 글\n${wrote.text}\n\n## 이미 선 개체\n${sceneState3d(app).join('\n') || '(없음)'}` }], callsSchema3d(chosen), signal, hooks, '구성');
  mark('구성');
  const raw3 = got3.raw.calls;
  const callCount = Array.isArray(raw3) ? raw3.length : 0;
  hooks.push('meta', `구성 ${seconds(got3.ms)} · 호출 ${callCount}개 · 생성 ${got3.tokens ?? '?'} 토큰`);
  for (const c of Array.isArray(raw3) ? raw3 as { tool?: unknown; args?: unknown }[] : []) hooks.push('call', `${String(c.tool)} ${JSON.stringify(c.args ?? {})}`);
  let standing = 0;
  const drawn: Drawn3 = await drawCalls3d(app, got3.raw, async (names) => { standing += 1; afterEachCall(names, '구성', standing); });
  for (const r of drawn.results) hooks.push(r.ok ? 'result' : 'error', `${r.call.function.name}: ${r.detail}`);
  const executed: ToolCall[] = [...drawn.executed];
  hooks.recordCalls?.(executed);
  await hooks.afterExecute?.('구성');

  // 구하는 양을 엔진이 직접 세워 잰다
  const gaps: string[] = [];
  if (!drawn.results.some((r) => r.ok)) gaps.push('그릴 개체를 하나도 세우지 못함');
  const measuredNow = await measureTarget3(app, target3);
  if (target3.kind === 'none') gaps.push('구하는 양이 선언되지 않아 엔진이 측정하지 못함');
  else if (measuredNow.problem) { gaps.push(`구하는 양을 세우지 못함: ${measuredNow.problem}`); hooks.push('error', `측정 실패: ${measuredNow.problem}`); }
  executed.push(...measuredNow.calls);
  hooks.recordCalls?.(executed);
  afterEachCall(registryOf3d(app).knownNames(), '측정', executed.length);
  await hooks.afterExecute?.('측정');
  mark('측정');

  // 문항 그림의 꼭짓점 위치로 시점을 맞춘다. 점이 모자라거나 한 평면 위이면 엔진 기본 시점이다
  {
    const reg = registryOf3d(app);
    const world: Record<string, V3> = {};
    for (const n of reg.knownNames()) { const o = reg.objectOf(n); if (o?.type === 'point' && o.position?.length === 3) world[n] = o.position as V3; }
    const fit = fitViewFromFigure(world, figurePoints);
    hooks.setView?.(fit);
    if (!figurePoints.length) hooks.push('meta', '그림 시점: 문항 그림의 꼭짓점 위치가 없어 엔진 기본 시점을 쓴다');
    else if (!fit) hooks.push('error', `그림 시점: 그림의 점 ${figurePoints.length}개 중 장면에 선 점이 4개 미만이거나 한 평면 위라 시점을 복원하지 못했다. 엔진 기본 시점을 쓴다`);
    else hooks.push(fit.rms > 0.08 ? 'error' : 'meta', `그림 시점 복원: 점 ${fit.points}개, 재현 오차 ${(fit.rms * 100).toFixed(1)}%${fit.rms > 0.08 ? '. 그림과 좌표의 모양이 어긋난다(좌표나 읽은 위치를 확인한다)' : ''}`);
  }

  const measured = measuredOf(app);
  const check = verifyAgainstMeasured(value, measured.map((m) => m.value), solved.measureValue);
  const independent = judgeIndependent(independentValue3(app, target3), solved.measureValue ?? value);
  hooks.push(independent.verdict === 'mismatch' ? 'error' : 'meta', `독립 재계산 ${independent.verdict} · ${independent.detail}`);
  hooks.setMeta({ independent });
  const okCount = drawn.results.filter((r) => r.ok).length;
  const failCount = drawn.rejected.length;
  const registry = registryOf3d(app);
  const standingPoints = new Set(registry.knownNames().filter((n) => registry.objectOf(n)?.type === 'point'));
  const missingPoints = problemPointNames(target).filter((n) => !standingPoints.has(n));
  const verdict = check.verdict === 'ok' ? `✓ ${check.detail}` : check.verdict === 'mismatch' ? `✗ ${check.detail}` : SOLUTION_FORMAT.verifyNone;
  const j = judge({ figure: '있음', failCount, missingPoints, verdict: check.verdict, detail: check.detail, gaps, answerIssue: answerFormatIssue(target, { choice: solved.choice, value }), answerMissing: value === null && !solved.choice, independent });
  if (variant) {
    if (j.acceptance === '성립') hooks.setMeta({ problem: target });
    else { hooks.setMeta({ rejected: j.reason }); hooks.push('error', `변형 문항 불성립: ${j.reason}. 검증을 통과한 문항만 낸다`); }
  }
  const figureLines = executed.map((c) => `- ${c.function.name} ${c.function.arguments}`);
  const elapsed = seconds(Date.now() - startedAt);
  hooks.setMeta({ answer, verdict, acceptance: j.acceptance as Acceptance, acceptanceReason: j.reason, measured: measured.map((m) => `- ${m.kind} ${m.name}: ${m.value}`), figure: figureLines.join('\n'), objectCount: okCount, failureCount: failCount, elapsed });
  hooks.setSolution([
    `**판정: ${j.acceptance}** — ${j.reason}`, '',
    SOLUTION_FORMAT.figure, '', figureLines.join('\n') || '(세운 개체 없음)', '',
    SOLUTION_FORMAT.steps, '', steps || '(없음)', '',
    SOLUTION_FORMAT.quick, '', quick || '(없음)', '',
    `**정답:** ${answer || '(없음)'}`, '', `**${verdict}**`,
  ].join('\n'));
  const stamp = `${elapsed} · ${j.acceptance} · 개체 ${okCount}${failCount ? ` · 실패 ${failCount}` : ''}${missingPoints.length ? ` · 누락 ${missingPoints.join(',')}` : ''}`;
  hooks.push(j.acceptance === '불성립' ? 'error' : 'assistant', `끝. ${stamp}`);
  return stamp;
}
